// src/adminDashboard/shared/components/VirtualizedList.tsx
// Virtual scrolling for large lists

import React, { useState, useRef, useCallback, memo } from 'react';

interface VirtualizedListProps<T> {
    items: T[];
    itemHeight?: number;
    containerHeight?: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    overscan?: number;
    className?: string;
}

const VirtualizedList = memo(<T extends { id?: string | number }>(props: VirtualizedListProps<T>) => {
    const {
        items = [],
        itemHeight = 56,
        containerHeight = 400,
        renderItem,
        overscan = 3,
        className = '',
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Calculate visible items
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visibleCount = Math.ceil(containerHeight / itemHeight) + overscan * 2;
    const endIndex = Math.min(items.length, startIndex + visibleCount);

    const visibleItems = items.slice(startIndex, endIndex);
    const totalHeight = items.length * itemHeight;
    const offsetY = startIndex * itemHeight;

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            className={`overflow-auto ${className}`}
            style={{ height: containerHeight }}
        >
            <div style={{ height: totalHeight, position: 'relative' }}>
                <div style={{ transform: `translateY(${offsetY}px)` }}>
                    {visibleItems.map((item, index) => (
                        <div
                            key={item.id || startIndex + index}
                            style={{ height: itemHeight }}
                        >
                            {renderItem(item, startIndex + index)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});

VirtualizedList.displayName = 'VirtualizedList';

export default VirtualizedList;
